import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { api, IGetStockroomSchema } from '../api/api';
import Loader from '../components/Loader';
import usePageTitle from '../utils/usePageTitle';
import { useProfileStore } from '../store/profileStore';
import { useAuthStore } from '../store/authStore';
import ChangeStatusModal from '../components/ChangeStatusModal';
import SetPlaceModal from '../components/SetPlaceModal';
import { useToast } from '../components/ToastContainer';

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #081C21;
  padding: 24px 48px;
  display: flex;
  flex-direction: column;
  width: 100%;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
`;


const Content = styled.div`
  display: flex;
  gap: 32px;

  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

const ImageContainer = styled.div`
  width: 350px;
  height: 350px;
  border-radius: 16px;
  overflow: hidden;
  flex-shrink: 0;

  @media (max-width: 1024px) {
    width: 100%;
    height: auto;
    aspect-ratio: 1/1;
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  background-color: transparent;
`;

const DetailsContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled.h1`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 700;
  font-size: 28px;
  color: #FFFFFF;
  margin: 0;
`;

const Subtitle = styled.h2`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 18px;
  color: #609F94;
  margin: 0;
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 24px;
`;

const InfoCard = styled.div`
  padding: 16px;
  background-color: #0E1F1F;
  border-radius: 8px;
  border: 1px solid #284640;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const CardTitle = styled.h4`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #24BA7D;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #284640;
  gap: 8px;
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
`;

const InfoLabel = styled.span`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 13px;
  color: #838D90;
  
  @media (max-width: 480px) {
    font-size: 12px;
    font-weight: 500;
  }
`;

const InfoValue = styled.span`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 13px;
  color: #FFFFFF;
  text-align: right;
  word-break: break-word;
  max-width: 60%;
  
  @media (max-width: 480px) {
    text-align: left;
    max-width: 100%;
    font-weight: 600;
  }
`;

const StatusContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background-color: #0E1F1F;
  border-radius: 8px;
  border: 1px solid #284640;
  gap: 12px;
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const StatusContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  @media (max-width: 480px) {
    justify-content: space-between;
    width: 100%;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border-radius: 16px;
  background-color: #24BA7D;
  color: #FFFFFF;
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 12px;
  text-align: center;
`;

const ErrorMessage = styled.div`
  background: #2d1b1b;
  color: #ff6b6b;
  padding: 20px;
  border-radius: 16px;
  text-align: center;
  border-left: 4px solid #f44336;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const ActionButton = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid #24BA7D;
  background-color: transparent;
  color: #24BA7D;
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background-color: #24BA7D;
    color: #FFFFFF;
  }
  
  @media (max-width: 480px) {
    padding: 10px 16px;
    font-size: 14px;
    width: 100%;
    text-align: center;
  }
`;

const LocationActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #284640;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const VendorRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const VendorContact = styled.a`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 13px;
  color: #24BA7D;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;


const StockroomItem: React.FC = () => {
  const { product_code } = useParams<{ product_code: string }>();
  const [product, setProduct] = useState<IGetStockroomSchema | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Получаем информацию об авторизации и профиле
  const { isAuthenticated } = useAuthStore();
  const { accessStockroom, fetchProfile } = useProfileStore();
  const { showSuccess, showError } = useToast();

  // Состояния для модальных окон
  const [changeStatusModal, setChangeStatusModal] = useState<{
    isOpen: boolean;
    productCode: string;
  }>({
    isOpen: false,
    productCode: '',
  });

  const [setPlaceModal, setSetPlaceModal] = useState<{
    isOpen: boolean;
    productCode: string;
  }>({
    isOpen: false,
    productCode: '',
  });

  // Динамический заголовок на основе данных о товаре
  usePageTitle(
    product
      ? `${product.product_brand} ${product.product_model}`
      : 'Информация о товаре'
  );


  useEffect(() => {
    const fetchProduct = async () => {
      if (!product_code) {
        setError('Код товара не указан');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await api.getStockroomItem(product_code);
        setProduct(data);
        
        // Загружаем профиль только для авторизованных пользователей
        if (isAuthenticated) {
          try {
            await fetchProfile();
          } catch (err) {
            // Игнорируем ошибки загрузки профиля, чтобы не блокировать отображение товара
            console.warn('Не удалось загрузить профиль:', err);
          }
        }
      } catch (err: any) {
        setError(err.message || 'Ошибка при загрузке товара');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [product_code, isAuthenticated, fetchProfile]);

  const refreshProductData = async () => {
    if (!product_code) return;

    try {
      const data = await api.getStockroomItem(product_code);
      setProduct(data);
    } catch (err: any) {
      showError('Ошибка при обновлении данных товара');
    }
  };

  const handleChangeStatus = () => {
    if (product?.product_code) {
      setChangeStatusModal({
        isOpen: true,
        productCode: product.product_code,
      });
    }
  };

  const handleChangeLocation = () => {
    if (product?.product_code) {
      setSetPlaceModal({
        isOpen: true,
        productCode: product.product_code,
      });
    }
  };

  const handleGenerateQRCode = async () => {
    if (!product?.product_code) return;

    try {
      const qrCodeResponse = await api.initProductInStockroomByCode(product.product_code);
      
      if (qrCodeResponse?.qr_code) {
        // Открываем QR-код в новом окне
        window.open(qrCodeResponse.qr_code, '_blank');
        showSuccess('QR-код успешно сгенерирован');
      } else {
        showError('Не удалось получить QR-код');
      }
    } catch (error: any) {
      showError('Ошибка при генерации QR-кода');
      console.error('Error generating QR code:', error);
    }
  };


  if (loading) {
    return (
      <PageContainer>
        <Container>
          <LoadingContainer>
            <Loader />
          </LoadingContainer>
        </Container>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <Container>
          <ErrorMessage>
            <h2>Ошибка загрузки</h2>
            <p>{error}</p>
          </ErrorMessage>
        </Container>
      </PageContainer>
    );
  }

  if (!product) {
    return (
      <PageContainer>
        <Container>
          <ErrorMessage>
            <h2>Товар не найден</h2>
            <p>Товар с кодом "{product_code}" не найден в системе</p>
          </ErrorMessage>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container>
        <Content>
          {product.product_image && (
            <ImageContainer>
              <Image 
                src={product.product_image} 
                alt={`${product.product_brand} ${product.product_model}`}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </ImageContainer>
          )}

          <DetailsContainer>
            <TitleSection>
              <Title>{product.product_brand} {product.product_model}</Title>
              <Subtitle>Код товара: {product.product_code}</Subtitle>
            </TitleSection>

            <InfoContainer>
              <StatusContainer>
                <StatusContent>
                  <CardTitle>Статус</CardTitle>
                  <StatusBadge>{product.product_status}</StatusBadge>
                </StatusContent>
                {isAuthenticated && accessStockroom && (
                  <ActionButton onClick={handleChangeStatus}>
                    Изменить статус
                  </ActionButton>
                )}
              </StatusContainer>

              {isAuthenticated && accessStockroom && (
                <InfoCard>
                  <CardHeader>
                    <CardTitle>Местоположение</CardTitle>
                  </CardHeader>
                  <InfoRow>
                    <InfoLabel>Локация</InfoLabel>
                    <InfoValue>{product.product_location?.location || 'Не указана'}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Место</InfoLabel>
                    <InfoValue>{product.product_location?.letter || ''}{product.product_location?.number || ''}</InfoValue>
                  </InfoRow>
                  <LocationActions>
                    <ActionButton onClick={handleChangeLocation}>
                      Сменить место
                    </ActionButton>
                  </LocationActions>
                </InfoCard>
              )}

              <InfoCard>
                <CardHeader>
                  <CardTitle>Детали продукта</CardTitle>
                </CardHeader>
                <InfoRow>
                  <InfoLabel>Бренд</InfoLabel>
                  <InfoValue>{product.product_brand}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Модель</InfoLabel>
                  <InfoValue>{product.product_model}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Цвет</InfoLabel>
                  <InfoValue>{product.product_color}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Состояние</InfoLabel>
                  <InfoValue>{product.product_condition}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>US размер</InfoLabel>
                  <InfoValue>{product.product_us_size}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>SKU</InfoLabel>
                  <InfoValue>{product.product_sku}</InfoValue>
                </InfoRow>
                {isAuthenticated && accessStockroom && (
                  <LocationActions>
                    <ActionButton onClick={handleGenerateQRCode}>
                      Открыть QR-код
                    </ActionButton>
                  </LocationActions>
                )}
              </InfoCard>

              {isAuthenticated && accessStockroom && (
                <InfoCard>
                  <CardHeader>
                    <CardTitle>Продавец</CardTitle>
                  </CardHeader>
                  <InfoRow>
                    <InfoLabel>Имя</InfoLabel>
                    <InfoValue>{product.product_vendor.username}</InfoValue>
                  </InfoRow>
                  {product.product_vendor.telegram && (
                    <InfoRow>
                      <InfoLabel>Telegram</InfoLabel>
                      <InfoValue>
                        <VendorContact
                          href={`https://t.me/${product.product_vendor.telegram.replace('@', '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {product.product_vendor.telegram}
                        </VendorContact>
                      </InfoValue>
                    </InfoRow>
                  )}
                  {product.product_vendor.vk && (
                    <InfoRow>
                      <InfoLabel>VK</InfoLabel>
                      <InfoValue>
                        <VendorContact
                          href={`https://vk.com/${product.product_vendor.vk}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {product.product_vendor.vk}
                        </VendorContact>
                      </InfoValue>
                    </InfoRow>
                  )}
                </InfoCard>
              )}
            </InfoContainer>
          </DetailsContainer>
        </Content>

        {/* Модальные окна для изменения статуса и места */}
        <ChangeStatusModal
          isOpen={changeStatusModal.isOpen}
          productCode={changeStatusModal.productCode}
          onClose={() => setChangeStatusModal({ isOpen: false, productCode: '' })}
          onSuccess={async () => {
            setChangeStatusModal({ isOpen: false, productCode: '' });
            await refreshProductData();
            showSuccess('Статус товара успешно изменен');
          }}
        />

        <SetPlaceModal
          isOpen={setPlaceModal.isOpen}
          productCode={setPlaceModal.productCode}
          onClose={() => setSetPlaceModal({ isOpen: false, productCode: '' })}
          onSuccess={async () => {
            setSetPlaceModal({ isOpen: false, productCode: '' });
            await refreshProductData();
            showSuccess('Место товара успешно изменено');
          }}
        />
      </Container>
    </PageContainer>
  );
};

export default StockroomItem;
